import { useEffect, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * Knative 环境选项
 */
interface KnativeEnvironmentOption {
  id: string; // 环境标识，如 "test", "staging", "prod"
  label: string; // 显示名称，如 "TEST 环境"
  clusterName: string; // 集群名称
  namespace: string; // 命名空间
  resourceRef: string; // 关联的 Knative Resource
  rawValue: string; // 原始配置值
}

/**
 * KnativeEnvironmentPicker
 *
 * 自定义字段：根据选中的 Knative System 获取已配置的环境信息
 *
 * 使用方式：
 * ```yaml
 * environment:
 *   title: 部署环境
 *   type: string
 *   ui:field: KnativeEnvironmentPicker
 *   ui:options:
 *     systemField: system
 * ```
 *
 * 工作流程：
 * 1. 监听 systemField 的变化
 * 2. 获取 System 实体
 * 3. 解析 hose.io/knative-dest-{env} 注解获取环境列表
 * 4. 用户选择环境后，输出环境标识
 */
interface KnativeEnvironmentPickerProps {
  onChange: (value: string | undefined) => void;
  rawErrors?: string[];
  required?: boolean;
  formData?: string;
  uiSchema?: Record<string, any>;
  idSchema?: { $id: string };
  schema?: { title?: string; description?: string };
  formContext?: { formData?: Record<string, any> };
}

export const KnativeEnvironmentPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
  formContext,
}: KnativeEnvironmentPickerProps) => {
  const catalogApi = useApi(catalogApiRef);

  const [environments, setEnvironments] = useState<KnativeEnvironmentOption[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  // 从 uiSchema 获取 system 字段名
  const systemField = uiSchema?.['ui:options']?.systemField || 'system';
  const selectedSystem = formContext?.formData?.[systemField];

  useEffect(() => {
    if (!selectedSystem) {
      setEnvironments([]);
      return;
    }

    const loadEnvironments = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // 获取 System 实体
        const systemEntity = await catalogApi.getEntityByRef(
          `system:default/${selectedSystem}`,
        );

        if (!systemEntity) {
          throw new Error(`System '${selectedSystem}' not found`);
        }

        // 验证是 Knative 类型的 System
        const poweredBy =
          systemEntity.metadata.annotations?.['hose.io/powered-by'];
        if (poweredBy !== 'knative') {
          throw new Error(`System '${selectedSystem}' 不是 Knative 类型`);
        }

        // 解析 System 的 Knative 环境注解
        const annotations = systemEntity.metadata.annotations || {};
        const envOptions: KnativeEnvironmentOption[] = [];

        // 支持的环境列表
        const envConfigs = [
          {
            destKey: 'hose.io/knative-dest-test',
            resourceKey: 'hose.io/knative-resource-test',
            id: 'test',
            label: 'TEST 环境',
          },
          {
            destKey: 'hose.io/knative-dest-staging',
            resourceKey: 'hose.io/knative-resource-staging',
            id: 'staging',
            label: 'STAGING 环境',
          },
          {
            destKey: 'hose.io/knative-dest-prod',
            resourceKey: 'hose.io/knative-resource-prod',
            id: 'prod',
            label: 'PROD 环境',
          },
        ];

        for (const envConfig of envConfigs) {
          const destValue = annotations[envConfig.destKey];
          const resourceRef = annotations[envConfig.resourceKey] || '';

          if (destValue) {
            // 解析 JSON 格式配置
            try {
              const destConfig = JSON.parse(destValue);
              if (destConfig && typeof destConfig === 'object') {
                envOptions.push({
                  id: envConfig.id,
                  label: envConfig.label,
                  clusterName: destConfig.clusterName || '',
                  namespace: destConfig.namespace || '',
                  resourceRef,
                  rawValue: destValue,
                });
              }
            } catch {
              // 解析失败，跳过
            }
          }
        }

        if (envOptions.length === 0) {
          setError(`System '${selectedSystem}' 未配置 Knative 环境`);
        }

        setEnvironments(envOptions);

        // 如果之前选中的环境在新的列表中不存在，清空选择
        if (formData && !envOptions.find(e => e.id === formData)) {
          onChange(undefined as any);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load Knative environments',
        );
        setEnvironments([]);
      } finally {
        setLoading(false);
      }
    };

    loadEnvironments();
  }, [selectedSystem, catalogApi, formData, onChange]);

  // 获取环境对应的颜色
  const getEnvColor = (envId: string): 'default' | 'primary' | 'secondary' => {
    switch (envId) {
      case 'test':
        return 'default';
      case 'staging':
        return 'primary';
      case 'prod':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const fieldId = idSchema?.$id || 'knative-environment-picker';

  // 如果没有选择 system，显示提示
  if (!selectedSystem) {
    return (
      <FormControl margin="normal" required={required} fullWidth disabled>
        <InputLabel htmlFor={fieldId}>
          {schema?.title || 'Knative Environment'}
        </InputLabel>
        <Select id={fieldId} value="">
          <MenuItem value="" disabled>
            请先选择 Knative System
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl
      margin="normal"
      required={required}
      error={(rawErrors?.length ?? 0) > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={fieldId}>
        {schema?.title || 'Knative Environment'}
      </InputLabel>
      <Select
        id={fieldId}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            {error}
          </MenuItem>
        )}
        {!loading && !error && environments.length === 0 && (
          <MenuItem value="" disabled>
            该系统未配置 Knative 环境
          </MenuItem>
        )}
        {environments.map(env => (
          <MenuItem key={env.id} value={env.id}>
            <Box display="flex" alignItems="center" width="100%">
              <Chip
                label={env.id.toUpperCase()}
                size="small"
                color={getEnvColor(env.id)}
                style={{ marginRight: 12, minWidth: 70 }}
              />
              <Box>
                <Typography variant="body2">
                  {env.namespace} @ {env.clusterName}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {(rawErrors?.length ?? 0) > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
      {formData &&
        (() => {
          const selectedEnv = environments.find(e => e.id === formData);
          return selectedEnv ? (
            <FormHelperText>
              已选择: {selectedEnv.label} - {selectedEnv.clusterName} 集群、
              {selectedEnv.namespace} namespace
            </FormHelperText>
          ) : null;
        })()}
    </FormControl>
  );
};

